import { Marked } from 'marked';
import markedBidi from 'marked-bidi';
import DOMPurify from 'dompurify';

// eslint-disable-next-line no-restricted-imports
const duoMarked = new Marked([{
  async: false,
  breaks: false,
  gfm: false
}, markedBidi()]);
const config = {
  ADD_TAGS: ['insert-code-snippet', 'copy-code', 'gl-markdown', '#text', 'gl-compact-markdown'],
  ADD_ATTR: ['data-canonical-lang', 'data-sourcepos', 'lang', 'data-src', 'img'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'button', 'svg', 'video', 'audio', 'embed', 'object'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick']
};
const handleImageElements = node => {
  var _node$nodeName;
  if (((_node$nodeName = node.nodeName) === null || _node$nodeName === void 0 ? void 0 : _node$nodeName.toLowerCase()) !== 'img') return node;
  const codeElement = node.ownerDocument.createElement('code');
  codeElement.textContent = node.outerHTML;
  node.parentNode.replaceChild(codeElement, node);
  return node;
};

/**
 * Validates if a URL is a safe relative URL without embedded malicious URLs
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is a safe relative URL
 */
function isRelativeUrlWithoutEmbeddedUrls(url) {
  // Check if the string starts with a slash but not with double slash (protocol-relative URLs)
  if (!url.startsWith('/') || url.startsWith('//')) {
    return false;
  }

  // Check for common URL schemes that might be embedded
  // URL Schemes Regex breakdown:
  // (?:(?:https?|ftp|mailto|tel|file|data|ssh|git):?\/\/) - Matches various protocols with optional colon and double slashes
  //   - https? - http or https
  //   - ftp - ftp protocol
  //   - mailto - mailto links
  //   - tel - telephone links
  //   - file - file protocol
  //   - data - data URIs
  //   - ssh - ssh protocol
  //   - git - git protocol
  // (?:www\.) - Alternatively matches URLs starting with www.
  // Flags: i (case insensitive)
  const urlSchemesRegex = /(?:(?:https?|ftp|mailto|tel|file|data|ssh|git):?\/\/)|(?:www\.)/i;

  // Return true only if no URL schemes are foundZSS%$
  return !urlSchemesRegex.test(url);
}
const sanitizeLinksHook = function () {
  let trustedUrls = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return node => {
    if (!node.hasAttribute('href')) {
      return;
    }
    const href = node.getAttribute('href');
    try {
      if (isRelativeUrlWithoutEmbeddedUrls(href)) {
        return;
      }
      // eslint-disable-next-line no-new
      new URL(href);
    } catch (e) {
      node.removeAttribute('href');
      return;
    }
    const urlHostname = new URL(href).hostname.toLowerCase();
    const isDocsGitlab = urlHostname === 'docs.gitlab.com';
    const isTrustedUrls = trustedUrls.includes(urlHostname);

    // Temporary fix until monolith side adds trustedUrls into the calling of component
    const isGitlab = urlHostname === 'gitlab.com';
    if (isDocsGitlab || isTrustedUrls || isGitlab) {
      return; // Do not modify links from allowed domains
    }

    // Create a new code element
    const codeElement = document.createElement('code');
    const paraElement = document.createElement('span');
    codeElement.appendChild(paraElement);
    // Move the node's children to the code element
    while (node.firstChild) {
      paraElement.appendChild(node.firstChild);
    }

    // Append the code element to the node
    node.appendChild(codeElement);

    // Preserve the href attribute
    paraElement.setAttribute('data-href', node.getAttribute('href'));
    node.removeAttribute('href');
  };
};
function isHtml(markup) {
  const src = markup.toString().trim();
  if (src.length === 0 || !src.startsWith('<') || !src.includes('>')) {
    return false; // fast-fail trivial cases
  }
  const doc = new DOMParser().parseFromString(src, 'text/html');

  // DOMParser drops invalid tags but inserts a <parsererror> element on failure
  // https://developer.mozilla.org/en-US/docs/Web/API/DOMParser/parseFromString#error_handling
  if (doc.querySelector('parsererror') !== null) {
    return false;
  }

  // Do we have at least one real element node inside <body>?
  return Array.from(doc.body.childNodes).some(n => n.nodeType === Node.ELEMENT_NODE);
}
function renderDuoChatMarkdownPreview(md) {
  let {
    trustedUrls = []
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!md) return '';
  DOMPurify.addHook('beforeSanitizeElements', handleImageElements);
  DOMPurify.addHook('afterSanitizeAttributes', sanitizeLinksHook(trustedUrls));
  const parsedMarkdown = isHtml(md) ? md : duoMarked.parse(md.toString());
  const sanitized = DOMPurify.sanitize(parsedMarkdown, config);
  DOMPurify.removeHook('beforeSanitizeElements');
  DOMPurify.removeHook('afterSanitizeAttributes');
  return sanitized;
}
function addDuoMarkdownPlugin(plugin) {
  duoMarked.use(plugin);
}

export { addDuoMarkdownPlugin, renderDuoChatMarkdownPreview };
